// Smokeball settings page — master admins paste OAuth credentials per
// domain. Secrets are stored server-side; we only ever display whether
// they're set, never the values themselves.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertCircle, Scale } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'sb-app-xxxxxxxx' },
  { key: 'client_secret', label: 'Client Secret', type: 'password' },
  { key: 'redirect_uri', label: 'Redirect URI', type: 'text', placeholder: 'https://your-app.com/oauth/smokeball/callback' },
  { key: 'refresh_token', label: 'Refresh Token', type: 'password' },
  { key: 'x_api_key', label: 'x-api-key', type: 'password' },
];

const AdminSmokeballSettingsPage = () => {
  const { domainId } = useParams();
  const [form, setForm] = useState({
    client_id: '', client_secret: '', redirect_uri: '',
    refresh_token: '', x_api_key: '', staging: false,
  });
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/smokeball/${domainId}/settings`)
      .then((r) => {
        setMeta(r.data);
        if (r.data?.configured) {
          setForm((f) => ({
            ...f,
            client_id: r.data.client_id || '',
            redirect_uri: r.data.redirect_uri || '',
            staging: !!r.data.staging,
          }));
        }
      })
      .catch(() => {/* ignore */});
  }, [domainId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/smokeball/${domainId}/settings`, form);
      toast.success('Smokeball settings saved');
      const r = await axios.get(`${API}/admin/smokeball/${domainId}/settings`);
      setMeta(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/admin/smokeball/${domainId}/test`);
      setTestResult(r.data);
      if (r.data?.ok) toast.success(r.data.message || 'Connection OK');
      else toast.error(r.data?.message || 'Test failed');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Test failed';
      setTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="smokeball-settings-page">
        <div className="flex items-center gap-3">
          <Scale className="w-7 h-7 text-indigo-500" />
          <div>
            <h1 className="text-2xl font-semibold text-white">Smokeball Integration</h1>
            <p className="text-sm text-white/50">
              Per-domain OAuth 2.0 credentials. Secrets are never read back —
              the page only shows which fields are configured.
            </p>
          </div>
        </div>

        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">OAuth Credentials</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="text-xs uppercase tracking-wider text-white/40 block mb-1">
                  {f.label}{' '}
                  {meta?.[`has_${f.key}`] && (
                    <span className="text-emerald-400 text-[10px]">● set</span>
                  )}
                </label>
                <input
                  type={f.type}
                  value={form[f.key] || ''}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  className="w-full bg-white/5 border border-white/10 text-white rounded px-2 py-2 text-sm focus:outline-none focus:border-indigo-500/50"
                  data-testid={`smokeball-field-${f.key}`}
                />
              </div>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                checked={form.staging}
                onChange={(e) => setForm({ ...form, staging: e.target.checked })}
                id="staging"
                data-testid="smokeball-staging"
              />
              <label htmlFor="staging" className="text-sm text-white/60">
                Use staging environment (datastaging-auth + stagingapi)
              </label>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving} data-testid="smokeball-save-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {saving ? 'Saving…' : 'Save credentials'}
              </Button>
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={testing || !meta?.configured}
                data-testid="smokeball-test-btn"
              >
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Test connection
              </Button>
            </div>

            {testResult && (
              <div
                className={`mt-3 p-3 rounded-md border ${
                  testResult.ok
                    ? 'bg-emerald-500/10 border-emerald-400/40 text-emerald-200'
                    : 'bg-red-500/10 border-red-400/40 text-red-200'
                }`}
                data-testid="smokeball-test-result"
              >
                <div className="flex items-start gap-2">
                  {testResult.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
                  <p className="text-sm">{testResult.message}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-base">Yabbit Webhook Setup</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-white/60">
            <p>Point your Yabbit &quot;Call Events&quot; webhook at:</p>
            <code className="block bg-black/30 px-3 py-2 rounded font-mono text-xs text-white/80 break-all">
              {(process.env.REACT_APP_BACKEND_URL || '')}/api/yabbit/webhook
            </code>
            <p>
              Include header <code className="text-white">X-Webhook-Secret</code> with the value of the
              <code className="text-white"> YABBIT_WEBHOOK_SECRET</code> env var (set by Emergent ops).
            </p>
            <p className="text-xs text-white/40">
              Events with state <code>ringing</code> trigger the popup. Internal extension-to-extension and star-code calls are auto-filtered.
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminSmokeballSettingsPage;
